import { getMembershipShareCanonicalStepId } from "./membershipShareRoutePolicy";
import { personalLoanFlow } from "../../navigation/flows/personalLoanFlow";
import type { paths } from "../../types/schema-lending";
import type { FlowType } from "@lending/navigation/types";
import type { MembershipShareAccessResponse } from "@lending/types";

type PersonalLoanApplication =
  paths["/v2/lending/personal_loan_applications/{uuid}/"]["get"]["responses"][200]["content"]["application/json"];

interface PersonalLoanRoutePolicyResult {
  canonicalStepId: string | null;
}

const PERSONAL_LOAN_WEB_FLOW_STEP_ORDER = personalLoanFlow.web.map((step) => step.id);

function isStepAheadOf(currentStepId: string, maxAllowedStepId: string): boolean {
  const currentIndex = PERSONAL_LOAN_WEB_FLOW_STEP_ORDER.indexOf(currentStepId);
  const maxAllowedIndex = PERSONAL_LOAN_WEB_FLOW_STEP_ORDER.indexOf(maxAllowedStepId);

  if (currentIndex === -1 || maxAllowedIndex === -1) {
    return false;
  }

  return currentIndex > maxAllowedIndex;
}

function getPersonalLoanCanonicalRoutePolicy(
  application: PersonalLoanApplication,
  currentStepId: string | null,
  membershipShareAccess?: MembershipShareAccessResponse,
  flowType: FlowType = "web"
): PersonalLoanRoutePolicyResult {
  const membershipShareCanonicalStepId = getMembershipShareCanonicalStepId(
    application.state,
    currentStepId,
    application.account_application_uuid,
    membershipShareAccess,
    flowType
  );

  if (membershipShareCanonicalStepId !== undefined) {
    return { canonicalStepId: membershipShareCanonicalStepId };
  }

  if (!currentStepId) {
    switch (application.state) {
      case "creating":
        return { canonicalStepId: "personal-info" };
      case "decisioning_queued":
      case "decisioning_processing":
        return { canonicalStepId: "credit-pull" };
      case "offers_generated":
      case "offer_accepted":
        return { canonicalStepId: "offers" };
      default:
        return { canonicalStepId: null };
    }
  }

  switch (application.state) {
    case "creating":
    case "decisioning_queued":
    case "decisioning_processing":
      return {
        canonicalStepId: isStepAheadOf(currentStepId, "credit-pull") ? "credit-pull" : null,
      };
    case "offers_generated":
      return { canonicalStepId: isStepAheadOf(currentStepId, "offers") ? "offers" : null };
    case "offer_accepted":
      return { canonicalStepId: "offers" };
    default:
      return { canonicalStepId: "submitted" };
  }
}

export { getPersonalLoanCanonicalRoutePolicy };
